import { createWithEqualityFn as create } from 'zustand/traditional';
import { shallow } from 'zustand/vanilla/shallow';

import SessionService from 'src/services/SessionService';
import UserStore from 'src/stores/user';
import SubscriptionsStore from 'src/stores/subscriptions';

import ProjectsStore from './projects';
import CollectionsStore from './collections';
import ChatsStore from './chats';
import KeywordsStore from './keywords';
import MessagesStore from './messages';
import FilterStore from './filter';

import type { IUserSignIn, IUserRegister } from 'src/types/User';

interface IState {
    error: string | null;
    isAuthorized: boolean;
    isCheckingChanges: boolean;
    hasSessionSnapshot: boolean;
}

interface IAction {
    setIsAuthorized: (value: boolean) => void;

    register: (values: IUserRegister) => Promise<any>;

    login: (values?: IUserSignIn) => Promise<any>;
    logout: () => void;

    getUserSessionRequiredData: typeof SessionService.getUserSessionRequiredData;
    checkUserDataChanges: () => Promise<boolean>;
}

const useSessionStore = create<IState & IAction>(
    (set, get) => ({
        error: null,

        isAuthorized: false,
        isCheckingChanges: false,
        hasSessionSnapshot: false,
        setIsAuthorized: (value) => {
            set({ isAuthorized: value });
        },

        getUserSessionRequiredData: async () => {
            try {
                const data = await SessionService.getUserSessionRequiredData();
                const { user, subscriptions, projects, collections } = data;

                UserStore.getState().setUserInfo(user);
                SubscriptionsStore.getState().setSubscriptions(subscriptions);
                ProjectsStore.getState().setProjects(projects);
                CollectionsStore.getState().setCollections(collections);

                set({
                    isAuthorized: true,
                    hasSessionSnapshot: true,
                });

                return Promise.resolve(data);
            } catch (err) {
                set({
                    isAuthorized: false,
                    hasSessionSnapshot: false,
                });

                return Promise.reject(err);
            }
        },

        checkUserDataChanges: async () => {
            // Предотвращаем множественные одновременные запросы
            if (get().isCheckingChanges) {
                console.log('⏳ Проверка уже выполняется, пропускаем...');
                return false;
            }

            try {
                set({ isCheckingChanges: true });
                console.log('🔍 Начинаем проверку изменений данных пользователя...');

                const currentUser = UserStore.getState();
                console.log('📊 Текущие данные пользователя:', {
                    onboardingStep: currentUser.onboardingStep,
                    isOnboardingCompleted: currentUser.isOnboardingCompleted,
                    isEmailConfirmed: currentUser.isEmailConfirmed,
                    bonusPoints: currentUser.bonusPoints,
                });

                const newUserData = await SessionService.getUserDataOnly();
                console.log('📡 Получены новые данные с сервера:', {
                    onboarding_step: newUserData.onboarding_step,
                    is_onboarding_completed: newUserData.is_onboarding_completed,
                    is_email_confirmed: newUserData.is_email_confirmed,
                    bonus_points: newUserData.bonus_points,
                });

                set({ hasSessionSnapshot: true });

                // Импортируем функцию для правильного сравнения onboardingStep
                const { getBeginWorkStepOrder } = await import('src/utils/beginWork');

                // Преобразуем строковый onboarding_step в числовой order для сравнения
                const newOnboardingStepOrder = getBeginWorkStepOrder(newUserData.onboarding_step, 1);
                console.log('🔄 Преобразование onboarding_step:', {
                    original: newUserData.onboarding_step,
                    converted: newOnboardingStepOrder,
                });

                // Проверяем изменения в критических полях
                const changes = {
                    onboardingStep: currentUser.onboardingStep !== newOnboardingStepOrder,
                    isOnboardingCompleted: currentUser.isOnboardingCompleted !== newUserData.is_onboarding_completed,
                    isEmailConfirmed: currentUser.isEmailConfirmed !== newUserData.is_email_confirmed,
                    bonusPoints: currentUser.bonusPoints !== newUserData.bonus_points,
                };

                const hasChanges = Object.values(changes).some(Boolean);
                console.log('🔍 Результат сравнения:', changes);

                if (hasChanges) {
                    console.log('🔄 Обнаружены изменения в данных пользователя, обновляем...');
                    UserStore.getState().setUserInfo(newUserData);
                    return true;
                }

                console.log('✅ Изменений не обнаружено');
                return false;
            } catch (err) {
                console.error('❌ Ошибка при проверке изменений пользователя:', err);
                return false;
            } finally {
                set({ isCheckingChanges: false });
            }
        },

        login: async (values) => {
            if (!values) {
                return;
            }

            try {
                await SessionService.signin(values);

                await get().getUserSessionRequiredData();

                set({
                    isAuthorized: true,
                });

                return Promise.resolve();
            } catch (error) {
                set({
                    isAuthorized: false,
                    error: '',
                });

                return Promise.reject(error);
            }
        },

        register: async (values) => {
            try {
                await SessionService.signup(values);
                return Promise.resolve();
            } catch (error) {
                return Promise.reject(error);
            }
        },

        logout: () => {
            SessionService.signOut();
            UserStore.getState().clearUser();
            ChatsStore.getState().setChats([]);
            SubscriptionsStore.getState().clear();
            ProjectsStore.getState().clear();
            CollectionsStore.getState().clear();
            KeywordsStore.getState().setKeywords([]);
            MessagesStore.getState().clear();
            FilterStore.getState().clearFilter();

            set({
                isAuthorized: false,
                error: null,
                hasSessionSnapshot: false,
            });
        },
    }),
    shallow
);

export default useSessionStore;
